/**
 * 미리보기 '한 장 통째로 보이기' 계산 (chat-doc-preview-fit — 2026-08-11).
 *
 * 전체보기에서 문서가 패널 **폭**에만 맞춰 그려지다 보니(pdf canvas width:100%, pptx fitMode:'contain')
 * A4·16:9 비율상 높이가 화면을 넘겨 아래가 잘렸다. 헤더에 수동 % 조절기를 두는 대신, 높이에 맞는
 * 배율을 계산해 자동으로 한 장이 들어오게 한다.
 *
 * pdf 는 CSS(max-height)만으로 해결되지만 pptx 는 렌더러가 px 를 굳혀서 zoom 값을 넘겨야 하므로
 * 계산식을 순수 함수로 분리해 둔다(DOM 없이 검증 가능).
 */

/** 슬라이드 라벨("Slide N") + 렌더러가 붙이는 하단 여백 보정치(px). */
export const SLIDE_CHROME_PX = 34;

/**
 * 한 장이 세로로 들어오는 zoom(%).
 *
 * 렌더러의 실효 배율은 `fitScale * zoom/100` 이고 fitScale 은 컨테이너 폭 기준이라,
 * "폭을 꽉 채웠을 때의 높이" 대비 "쓸 수 있는 높이" 비율이 그대로 zoom 이 된다.
 * 100 을 넘기지 않는다 — 폭이 이미 제약이면 기존 동작(폭 꽉 채움) 그대로 둔다.
 * 값이 이상하면(0·음수·NaN) 안전하게 100 을 돌려 기존 동작으로 폴백한다.
 */
export function computeFitZoom(slideW: number, slideH: number, containerW: number, availH: number): number {
  if (!Number.isFinite(slideW) || !Number.isFinite(slideH)) return 100;
  if (!Number.isFinite(containerW) || !Number.isFinite(availH)) return 100;
  if (slideW <= 0 || slideH <= 0 || containerW <= 0 || availH <= 0) return 100;
  const heightAtFullWidth = containerW * (slideH / slideW);
  const pct = Math.floor((availH / heightAtFullWidth) * 100);
  // 너무 작아지면 글자가 안 보인다 — 하한 20%. 그 아래는 사용자가 스크롤하는 편이 낫다.
  return Math.max(20, Math.min(100, pct));
}

/** 사용자 확대/축소 배율(%) 범위 — 100 = 자동 맞춤 그대로. */
export const PREVIEW_ZOOM_MIN = 50;
export const PREVIEW_ZOOM_MAX = 300;
export const PREVIEW_ZOOM_STEP = 10;
export const PREVIEW_ZOOM_DEFAULT = 100;

export function clampZoom(v: number): number {
  if (!Number.isFinite(v)) return PREVIEW_ZOOM_DEFAULT;
  return Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, Math.round(v)));
}

/** 열 배치에서 한 칸(슬라이드/페이지 한 장)이 쓸 수 있는 폭(px). */
export function computeCellWidth(containerW: number, cols: number, gap: number): number {
  if (!Number.isFinite(containerW) || containerW <= 0) return 0;
  const n = Math.max(1, Math.floor(cols));
  const g = Number.isFinite(gap) && gap > 0 ? gap : 0;
  return Math.max(0, (containerW - g * (n - 1)) / n);
}

/**
 * pptx 렌더러에 넘길 최종 zoom(%) — 열 배치 + 높이 맞춤 + 사용자 확대/축소를 한 번에 반영.
 *
 * 렌더러의 실효 배율은 `fitScale * zoom/100`, fitScale 은 **컨테이너 폭** 기준이다. 따라서
 * 슬라이드 렌더 폭 = containerW * zoom/100 이고, 이를 한 칸 폭에 맞추면 zoom = 100*cellW/containerW.
 * 여기에 세로 상한(computeFitZoom)을 함께 적용해 작은 쪽을 쓰고(= contain), 사용자 배율을 곱한다.
 */
export function computeSlideZoom(args: {
  slideW: number;
  slideH: number;
  containerW: number;
  availH: number;
  cols: number;
  gap: number;
  userZoom: number;
}): number {
  const { slideW, slideH, containerW, availH, cols, gap, userZoom } = args;
  if (!Number.isFinite(containerW) || containerW <= 0) return 100;
  const cellW = computeCellWidth(containerW, cols, gap);
  const widthZoom = cellW > 0 ? (cellW / containerW) * 100 : 100;
  const heightZoom = computeFitZoom(slideW, slideH, containerW, availH);
  const base = Math.min(widthZoom, heightZoom);
  const scaled = (base * clampZoom(userZoom)) / 100;
  // 렌더러가 감당할 범위로만 — 0 이하는 렌더 실패, 과대는 메모리 폭증.
  return Math.max(5, Math.min(500, Math.round(scaled * 100) / 100));
}
